import React, { useEffect } from 'react';
import { AiOutlineProduct } from 'react-icons/ai';
import { FaTrashAlt } from 'react-icons/fa';
import { HiOutlineTemplate } from 'react-icons/hi';
import { HiCalendarDays } from 'react-icons/hi2';
import { IoDocumentText, IoDocumentsOutline } from 'react-icons/io5';
import { MdEventAvailable, MdPerson3, MdSimCardAlert } from 'react-icons/md';
import { PiFolder, PiRankingDuotone } from 'react-icons/pi';
import { TbCashRegister } from 'react-icons/tb';
import { FaClipboardList } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from 'rizzui';
import SimpleBar from 'simplebar-react';
import { permissionsGroup, usePrivilege } from '../../components/priviledge/PriviledgeProvider';
import { setSelectedTab } from '../../redux/settingsSlice';
import { RootState } from '../../redux/store';
import { menuRoutes } from '../../router/menu';
import cn from '../../utils/classNames';
import { useSettingsProvider } from './provider/SettingsProvider';
import { GiReceiveMoney } from 'react-icons/gi';

interface MenuLink {
    label: string;
    path: string;
    icon: JSX.Element;
    permission?: { action: string; group: permissionsGroup };
};

const menuLinks: MenuLink[] = [
    { label: 'Pedidos', path: menuRoutes.settings.path + menuRoutes.settings.orders, icon: <FaClipboardList /> },
    { label: 'Contratos', path: menuRoutes.settings.path + menuRoutes.settings.contracts, icon: <IoDocumentText /> },
    { label: 'Facturación', path: menuRoutes.settings.path + '#invoices', icon: <TbCashRegister /> },
    { label: 'Incidencias', path: menuRoutes.settings.path + '#incidences', icon: <MdSimCardAlert /> },
    { label: 'Tutores', path: menuRoutes.settings.path + menuRoutes.settings.tutors, icon: <MdPerson3 /> },
    { label: 'Días Festivos', path: menuRoutes.settings.path + menuRoutes.settings.holidays, icon: <HiCalendarDays /> },
    { label: 'Plantillas', path: menuRoutes.settings.path + menuRoutes.settings.templates, icon: <HiOutlineTemplate />, permission: { action: 'manage_company_email_templates', group: 'user' } },
    { label: 'Eventos', path: menuRoutes.settings.path + menuRoutes.settings.events, icon: <MdEventAvailable />, permission: { action: 'list_events', group: 'events' } },
    { label: 'Rankings', path: menuRoutes.settings.path + menuRoutes.settings.rankings, icon: <PiRankingDuotone />, permission: { action: 'admin_rankings', group: 'rankings' } },
    { label: 'Tipos de documentos', path: menuRoutes.settings.path + menuRoutes.settings.document_types, icon: <IoDocumentsOutline />, permission: { action: 'admin_document_type', group: 'document_type' } },
    { label: 'Tipos de Productos', path: menuRoutes.settings.path + menuRoutes.settings.product_types, icon: <AiOutlineProduct />, permission: { action: 'admin_product_types', group: 'product_types' } },
    { label: 'Agrupaciones Fundae', path: menuRoutes.settings.path + menuRoutes.settings.fundae_groupings, icon: <PiFolder />, permission: { action: 'admin_contracts', group: 'contracts' } },
    { label: 'Comisiones', path: menuRoutes.settings.path + menuRoutes.settings.commissions, icon: <GiReceiveMoney />, permission: { action: 'show_user_commissions', group: 'user' } }
];

const SettingsSidebar: React.FC = () => {

    const { setShowCacheModal } = useSettingsProvider();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { userCan } = usePrivilege();
    const { selectedTab } = useSelector((state: RootState) => state.settings);
    const location = useLocation();
    const pathname = location.pathname;
    const hash = location.hash.replace('#', '');

    useEffect(() => {
        const availablePaths = menuLinks.map((link) => link.path);
        const fallbackPath = menuRoutes.settings.path + menuRoutes.settings.orders;
        const targetPath = availablePaths.includes(selectedTab) ? selectedTab : fallbackPath;

        if (pathname !== targetPath) {
            navigate(targetPath);
        }
    }, [selectedTab, pathname, navigate]);

    return (
        <SimpleBar className="lg:pt-6 lg:ps-6">
            <div className="flex flex-col items-left justify-start h-full p-4">
                <h2 className="uppercase font-bold text-gray-400 mb-4">Menú Navegación</h2>
                <div className="flex flex-row lg:flex-col text-nowrap flex-nowrap overflow-auto gap-1 scrollbar-thin">
                    {menuLinks.map((link) => {
                        let isActive = (pathname === (link.path as string));
                        let isActiveMultiple = link.path.split('#').includes(hash);

                        if (link.permission) {
                            const hasAccess = userCan(link.permission.action, link.permission.group as permissionsGroup);
                            if (!hasAccess) return null;
                        }

                        return (
                            <Link to={link.path}
                                key={link.label}
                                className={cn("flex flex-row flex-nowrap items-center gap-2 py-2 px-4 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-lg",
                                    (isActive || isActiveMultiple) ? 'bg-primary-100 text-gray-800 hover:bg-primary-300' : '',
                                )}
                                onClick={() => dispatch(setSelectedTab(link.path))}
                            >
                                {link.icon}
                                {link.label}
                            </Link>
                        )
                    })}
                    <Button
                        id="clear-cache-button"
                        className='mt-2'
                        onClick={() => setShowCacheModal(true)}
                    >
                        <FaTrashAlt className='me-2' />&nbsp;
                        Limpiar Caché
                    </Button>
                </div>
            </div>
        </SimpleBar>
    );
};

export default SettingsSidebar;
